import React, {useEffect, useState, useRef, useCallback} from "react";
import {Button} from "@mui/material";
// @ts-ignore
import {CSVLink} from "react-csv";
import {MoonLoader} from "react-spinners";
import {CloudDownload} from "@mui/icons-material";
import tradeService from "../../services/trade.service";
import {Trade, vocsetColumns} from "../../types/APITypes";
import VOCSETLayout from "../../views/VOCSETLayout";
import {dropdownItems} from "../../views/dropdownItems";
import {
    DataSource,
    DataSourceDataParams,
    InfiniteTable,
    InfiniteTablePropColumns,
} from "@infinite-table/infinite-react";

const licenseKey = process.env.REACT_APP_INFINITE_TABLE_LICENSE_KEY;
// @ts-ignore
const tradeColumns: InfiniteTablePropColumns<Trade> = vocsetColumns;

const HistoricalTradeDashboard = () => {
    const [trades, setTrades] = useState<Trade[]>([]);
    const [loading, setLoading] = useState(true);
    const [size, setSize] = useState(50);
    const [totalPages, setTotalPages] = useState<number | undefined>();
    const tableRef = useRef<HTMLDivElement>(null);
    const isFetchingRef = useRef(false);
    const currentPageRef = useRef(0);

    const fetchTrades = async (nextPage: number = 0) => {
        isFetchingRef.current = true;
        setLoading(true);
        try {
            const response = await tradeService.getHistoricalTrades(nextPage, size);
            const fetchedTrades = response.result;

            setTrades((prev) =>
                nextPage === 0 ? fetchedTrades : [...prev, ...fetchedTrades]
            );

            currentPageRef.current = nextPage;
            setTotalPages(response?.totalPages ?? 1);
        } catch (error) {
            console.error("Failed to fetch historical trades:", error);
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    };

    useEffect(() => {
        fetchTrades(0);
    }, []);

    useEffect(() => {
        const measureTableHeight = () => {
            if (tableRef.current) {
                const height = tableRef.current.clientHeight;
                const estimatedRowHeight = 50;
                const rowsPerPage = Math.floor(height / estimatedRowHeight) + 5;
                setSize(rowsPerPage);
            }
        };

        setTimeout(measureTableHeight, 500);
        window.addEventListener("resize", measureTableHeight);
        return () => {
            window.removeEventListener("resize", measureTableHeight);
        };
    }, [trades]);

    const [dataParams, setDataParams] = useState<Partial<DataSourceDataParams<Trade>>>({
        groupBy: [],
        sortInfo: undefined,
        livePaginationCursor: null,
    });

    const onDataParamsChange = useCallback(
        (dataParams: DataSourceDataParams<Trade>) => {
            const params = {
                groupBy: dataParams.groupBy,
                sortInfo: dataParams.sortInfo,
                livePaginationCursor: dataParams.livePaginationCursor,
            };
            setDataParams(params);

            const isLastPage = currentPageRef.current + 1 >= (totalPages ?? Infinity);

            if (!isFetchingRef.current && !isLastPage) {
                fetchTrades(currentPageRef.current + 1);
            }
        },
        [totalPages]
    );

    return (
        <VOCSETLayout
            dropdownItems={dropdownItems}
            style={{display: "flex", flexFlow: "column", flex: 1}}
        >
            {loading && trades.length === 0 ? (
                <div style={{display: "flex", justifyContent: "center", alignItems: "center", height: "100vh"}}>
                    <MoonLoader color="#282c34"/>
                </div>
            ) : (
                <>
                    <div style={{marginBottom: "16px"}}>
                        <CSVLink
                            data={Array.isArray(trades) ? trades : []}
                            headers={
                                trades.length > 0
                                    ? Object.keys(trades[0]).map((key) => ({label: key, key}))
                                    : []
                            }
                            filename="trades.csv"
                        >
                            <Button
                                component="span"
                                variant="contained"
                                startIcon={<CloudDownload/>}
                                disabled={trades.length === 0}
                            >
                                Export Trades
                            </Button>
                        </CSVLink>
                    </div>

                    {trades.length === 0 ? (
                        <div>
                            <p>No historical trades found.</p>
                        </div>
                    ) : (
                        <div
                            ref={tableRef}
                            className="infinite-theme-mode--light"
                            style={{display: "flex", flexFlow: "column", flex: 1}}
                        >
                            <DataSource<Trade>
                                data={trades}
                                defaultFilterValue={[]}
                                filterDelay={0}
                                primaryKey="tradeID"
                                onDataParamsChange={onDataParamsChange}
                                livePagination
                            >
                                <InfiniteTable<Trade> licenseKey={licenseKey} columns={tradeColumns}/>
                            </DataSource>
                        </div>
                    )}
                </>
            )}
        </VOCSETLayout>
    );
};

export default HistoricalTradeDashboard;
